import { useCallback, useEffect, useState } from 'react';
import { formsApi } from '../api/formsApi';
import type { SecretLink, SecretLinkCreated } from '../api/types';

function statusOf(link: SecretLink): string {
  if (link.revokedAt) return 'Revoked';
  if (link.usedAt) return 'Used';
  if (link.expiresAt && new Date(link.expiresAt).getTime() <= Date.now()) return 'Expired';
  return 'Active';
}

/** Manage secret links for a SecretLink-mode form: create (shown once), list, revoke. */
export function SecretLinksPanel({ formId }: { formId: string }) {
  const [links, setLinks] = useState<SecretLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [label, setLabel] = useState('');
  const [singleUse, setSingleUse] = useState(false);
  const [expiry, setExpiry] = useState('');
  const [created, setCreated] = useState<SecretLinkCreated | null>(null);
  const [copied, setCopied] = useState(false);

  const refresh = useCallback(() => {
    setLoading(true);
    formsApi
      .listSecretLinks(formId)
      .then(setLinks)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load links'))
      .finally(() => setLoading(false));
  }, [formId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setCopied(false);
    try {
      const link = await formsApi.createSecretLink(formId, {
        label: label.trim() || null,
        singleUse,
        expiresAt: expiry ? new Date(expiry).toISOString() : null,
      });
      setCreated(link);
      setLabel('');
      setSingleUse(false);
      setExpiry('');
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create link');
    } finally {
      setBusy(false);
    }
  }

  async function revoke(linkId: string) {
    setBusy(true);
    setError(null);
    try {
      await formsApi.revokeSecretLink(formId, linkId);
      if (created?.id === linkId) setCreated(null);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke link');
    } finally {
      setBusy(false);
    }
  }

  async function copy(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div className="secret-links">
      <h3>Secret links</h3>
      <p className="muted">
        Anyone with a secret link can open this form. The link is shown once when created — copy it then.
      </p>

      <form className="create-link" onSubmit={create}>
        <input
          type="text"
          placeholder="Label (optional, e.g. recipient)"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
        <label className="field-checkbox">
          <input type="checkbox" checked={singleUse} onChange={(e) => setSingleUse(e.target.checked)} />
          Single use
        </label>
        <label className="field-inline">
          Expires
          <input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} />
        </label>
        <button type="submit" disabled={busy}>Create link</button>
      </form>

      {error && <p className="error">{error}</p>}

      {created && (
        <div className="link-created">
          <p className="muted">New link{created.label ? ` — ${created.label}` : ''} (copy it now):</p>
          <div className="link-url-row">
            <code className="link-url">{created.url}</code>
            <button type="button" onClick={() => copy(created.url)}>{copied ? 'Copied' : 'Copy'}</button>
          </div>
        </div>
      )}

      {loading ? (
        <p>Loading…</p>
      ) : links.length === 0 ? (
        <p className="muted">No links yet.</p>
      ) : (
        <table className="links-table">
          <thead>
            <tr>
              <th>Label</th>
              <th>Status</th>
              <th>Single use</th>
              <th>Expires</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {links.map((l) => (
              <tr key={l.id}>
                <td>{l.label ?? <span className="muted">—</span>}</td>
                <td>{statusOf(l)}</td>
                <td>{l.singleUse ? 'Yes' : '—'}</td>
                <td>{l.expiresAt ? new Date(l.expiresAt).toLocaleDateString() : '—'}</td>
                <td>{new Date(l.createdAt).toLocaleDateString()}</td>
                <td>
                  {l.active && (
                    <button type="button" className="link-revoke" disabled={busy} onClick={() => revoke(l.id)}>
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
